import type { Shape, Vec2 } from '@sw/schema'
import { add, closestPointOnSegment, distance, distanceToSegment, length, scale, sub } from './vec.js'

export function pointInShape(point: Vec2, shape: Shape): boolean {
  if (shape.kind === 'circle') {
    return distance(point, shape.center) <= shape.radiusM
  }
  return pointInPolygon(point, shape.points)
}

/** Ray casting. Points exactly on an edge may fall either way; callers use hysteresis. */
export function pointInPolygon(point: Vec2, polygon: readonly Vec2[]): boolean {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]
    const b = polygon[j]
    const straddles = a.y > point.y !== b.y > point.y
    if (straddles && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside
    }
  }
  return inside
}

/**
 * Distance to the boundary: negative inside, positive outside, zero on the
 * edge.
 *
 * One signed number drives three different behaviours — zone crossfades, the
 * enter/exit margins that stop GPS jitter from machine-gunning a trigger, and
 * rolloff for a listener standing outside a region — so they can never
 * disagree about where the edge is.
 */
export function signedDistanceToShape(point: Vec2, shape: Shape): number {
  if (shape.kind === 'circle') {
    return distance(point, shape.center) - shape.radiusM
  }
  const polygon = shape.points
  let nearest = Number.POSITIVE_INFINITY
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    nearest = Math.min(nearest, distanceToSegment(point, polygon[j], polygon[i]))
  }
  return pointInPolygon(point, polygon) ? -nearest : nearest
}

/**
 * The area-weighted centre of a region, so a long, thin region does not appear
 * to emanate from one end.
 *
 * Note that a concave region's centroid can fall outside the region — an
 * L-shaped courtyard's centre lands in the missing corner. Use
 * {@link nearestPointOnShape} to place a zone's sound relative to a listener;
 * the centroid is for map framing and for far-field direction, where the
 * difference is inaudible.
 */
export function shapeCentroid(shape: Shape): Vec2 {
  if (shape.kind === 'circle') return shape.center

  const polygon = shape.points
  let twiceArea = 0
  let cx = 0
  let cy = 0
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j]
    const b = polygon[i]
    const cross = a.x * b.y - b.x * a.y
    twiceArea += cross
    cx += (a.x + b.x) * cross
    cy += (a.y + b.y) * cross
  }

  if (twiceArea === 0) {
    // Degenerate (collinear) polygon: fall back to the average of the points.
    const sum = polygon.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 })
    return { x: sum.x / polygon.length, y: sum.y / polygon.length }
  }

  return { x: cx / (3 * twiceArea), y: cy / (3 * twiceArea) }
}

/**
 * The point of the region closest to `point`, which is where a zone should
 * sound like it is coming from for a listener standing outside it. Returns the
 * listener's own position when they are already inside.
 */
export function nearestPointOnShape(point: Vec2, shape: Shape): Vec2 {
  if (pointInShape(point, shape)) return point

  if (shape.kind === 'circle') {
    const offset = sub(point, shape.center)
    const away = length(offset)
    if (away === 0) return { x: shape.center.x + shape.radiusM, y: shape.center.y }
    return add(shape.center, scale(offset, shape.radiusM / away))
  }

  const polygon = shape.points
  let best = polygon[0]
  let bestDistance = Number.POSITIVE_INFINITY
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const candidate = closestPointOnSegment(point, polygon[j], polygon[i]).point
    const away = distance(point, candidate)
    if (away < bestDistance) {
      bestDistance = away
      best = candidate
    }
  }
  return best
}

/** Bounding box, for map fitting and cheap broad-phase culling. */
export function shapeBounds(shape: Shape): { min: Vec2; max: Vec2 } {
  if (shape.kind === 'circle') {
    return {
      min: { x: shape.center.x - shape.radiusM, y: shape.center.y - shape.radiusM },
      max: { x: shape.center.x + shape.radiusM, y: shape.center.y + shape.radiusM },
    }
  }
  const xs = shape.points.map((p) => p.x)
  const ys = shape.points.map((p) => p.y)
  return {
    min: { x: Math.min(...xs), y: Math.min(...ys) },
    max: { x: Math.max(...xs), y: Math.max(...ys) },
  }
}
